import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Edit2, Trash2, ChevronLeft, PanelLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Conversation } from "@/hooks/useAIConversations";

interface AIChatSidebarProps {
  conversations: {
    today: Conversation[];
    lastSevenDays: Conversation[];
    lastThirtyDays: Conversation[];
    older: Conversation[];
  };
  currentConversationId: string | null;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onNewConversation: () => void;
  onSelectConversation: (id: string) => void;
  onRenameConversation: (id: string, newTitle: string) => void;
  onDeleteConversation: (id: string) => void;
}

export function AIChatSidebar({
  conversations,
  currentConversationId,
  isCollapsed,
  onToggleCollapse,
  onNewConversation,
  onSelectConversation,
  onRenameConversation,
  onDeleteConversation,
}: AIChatSidebarProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const handleDeleteClick = (id: string) => {
    setConversationToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (conversationToDelete) {
      await onDeleteConversation(conversationToDelete);
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    }
  };

  const handleEditClick = (id: string, currentTitle: string) => {
    setEditingId(id);
    setEditTitle(currentTitle);
  };

  const handleSaveEdit = () => {
    if (editingId && editTitle.trim()) {
      onRenameConversation(editingId, editTitle.trim());
      setEditingId(null);
      setEditTitle('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditTitle('');
    }
  };

  const ConversationItem = ({ conversation }: { conversation: Conversation }) => {
    const isActive = conversation.id === currentConversationId;
    const isEditing = editingId === conversation.id;

    if (isCollapsed) {
      return null;
    }

    if (isEditing) {
      return (
        <div className="px-3 py-1.5">
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleSaveEdit}
            onKeyDown={handleKeyDown}
            className="h-8"
            autoFocus
          />
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors group",
          isActive ? "bg-accent" : "hover:bg-accent/50"
        )}
        onClick={() => onSelectConversation(conversation.id)}
      >
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(conversation.id, conversation.title);
            }}
          >
            <Edit2 className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(conversation.id);
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
        <span className="flex-1 truncate text-sm">{conversation.title}</span>
      </div>
    );
  };

  const ConversationGroup = ({ title, conversations: convos }: { title: string; conversations: Conversation[] }) => {
    if (convos.length === 0) return null;

    return (
      <div className="space-y-0.5">
        <h3 className="text-xs font-medium text-muted-foreground px-3 py-1">{title}</h3>
        {convos.map((conversation) => (
          <ConversationItem key={conversation.id} conversation={conversation} />
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Botão flutuante quando colapsado */}
      {isCollapsed && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="fixed top-4 left-4 z-50 h-10 w-10 bg-background border shadow-lg hover:bg-accent"
        >
          <PanelLeft className="h-5 w-5" />
        </Button>
      )}

      <div
        className={cn(
          "flex flex-col h-full border-r bg-card transition-all duration-300 overflow-hidden",
          isCollapsed ? "w-0 border-r-0" : "w-64"
        )}
      >
        <div className="p-4 border-b flex items-center justify-between shrink-0">
          <Button
            onClick={onNewConversation}
            className="flex-1 mr-2"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Conversa
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-3 space-y-3">
            {conversations.today.length > 0 && (
              <ConversationGroup title="Hoje" conversations={conversations.today} />
            )}
            {conversations.lastSevenDays.length > 0 && (
              <ConversationGroup title="Últimos 7 dias" conversations={conversations.lastSevenDays} />
            )}
            {conversations.lastThirtyDays.length > 0 && (
              <ConversationGroup title="Últimos 30 dias" conversations={conversations.lastThirtyDays} />
            )}
            {conversations.older.length > 0 && (
              <ConversationGroup title="Mais antigos" conversations={conversations.older} />
            )}
          </div>
        </ScrollArea>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir conversa?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. A conversa será permanentemente excluída.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDelete}>
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}
